/**
 * WorkspaceOnboarding - 워크스페이스 온보딩 화면
 *
 * 첫 실행 시 사용자가 워크스페이스를 설정하도록 안내하는 화면입니다.
 * "기존 연결" 또는 "새로 생성" 중 선택할 수 있습니다.
 */

import { useState } from 'react'
import { WorkspaceAddModal } from './WorkspaceAddModal'
import { SecurityNotice } from '../../../shared/components/ui/SecurityNotice'
import type { WorkspaceCreationMode } from '../types'

/**
 * WorkspaceOnboarding 컴포넌트
 */
export function WorkspaceOnboarding() {
  const [showModal, setShowModal] = useState(false)
  const [selectedMode, setSelectedMode] = useState<WorkspaceCreationMode>('connect')

  /**
   * 모달 열기
   */
  const handleOpenModal = (mode: WorkspaceCreationMode) => {
    setSelectedMode(mode)
    setShowModal(true)
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center px-4">
      <div className="max-w-md w-full">
        {/* 로고 */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100 mb-2">
            ourWallet
          </h1>
          <p className="text-gray-600 dark:text-gray-400">
            가계부를 연결하여 시작하세요
          </p>
        </div>

        {/* 옵션 카드 */}
        <div className="space-y-4">
          {/* 기존 스프레드시트 연결 */}
          <button
            onClick={() => handleOpenModal('connect')}
            className="w-full p-6 bg-white dark:bg-gray-800 rounded-lg shadow-md
                     border-2 border-transparent hover:border-primary-500
                     transition-all duration-200 text-left"
          >
            <div className="flex items-start space-x-4">
              <div className="text-4xl">📊</div>
              <div className="flex-1">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-1">
                  기존 스프레드시트 연결
                </h2>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  이미 만들어진 가계부 스프레드시트가 있나요?
                  <br />
                  스프레드시트 ID를 입력하여 바로 연결하세요.
                </p>
              </div>
            </div>
          </button>

          {/* 새 스프레드시트 생성 */}
          <button
            onClick={() => handleOpenModal('create')}
            className="w-full p-6 bg-white dark:bg-gray-800 rounded-lg shadow-md
                     border-2 border-transparent hover:border-primary-500
                     transition-all duration-200 text-left"
          >
            <div className="flex items-start space-x-4">
              <div className="text-4xl">✨</div>
              <div className="flex-1">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-1">
                  새 스프레드시트 생성
                </h2>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  처음 시작하시나요?
                  <br />
                  필요한 시트들을 자동으로 생성해드려요.
                </p>
              </div>
            </div>
          </button>
        </div>

        {/* 보안 안내 */}
        <div className="mt-6">
          <SecurityNotice />
        </div>

        {/* 도움말 */}
        <div className="mt-8 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
          <h3 className="text-sm font-semibold text-blue-900 dark:text-blue-100 mb-2">
            💡 어떤 방법을 선택해야 하나요?
          </h3>
          <ul className="text-xs text-blue-700 dark:text-blue-300 space-y-1">
            <li>• <strong>기존 연결:</strong> ourWallet 스프레드시트가 이미 있는 경우</li>
            <li>• <strong>새로 생성:</strong> 처음 사용하거나 새로운 가계부를 만드는 경우</li>
          </ul>
        </div>
      </div>

      {/* 워크스페이스 추가 모달 */}
      {showModal && (
        <WorkspaceAddModal
          onClose={() => setShowModal(false)}
          initialMode={selectedMode}
        />
      )}
    </div>
  )
}
